/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-28
 */

import React from 'react';
import { IconButton, Tooltip, useTheme } from '@mui/material';
import { Wifi, CloudDone, HelpOutline } from '@mui/icons-material';
import { useCacheStatus } from '../../hooks/useCacheStatus';

/**
 * Props for the CacheStatusIndicator component.
 */
interface CacheStatusIndicatorProps {
  /**
   * Size of the icon in pixels.
   * @default 20
   */
  size?: number;
  /**
   * Whether to show the indicator.
   * @default true
   */
  show?: boolean;
}

/**
 * Cache status indicator component that shows whether content is live or cached.
 * Displays a subtle icon in the header to indicate the current cache status.
 *
 * @param props - The component props
 * @returns {React.ReactElement} The cache status indicator component
 *
 * @example
 * return (
 *   <CacheStatusIndicator />
 * );
 *
 * @example
 * // Custom size
 * return (
 *   <CacheStatusIndicator size={24} />
 * );
 */
const CacheStatusIndicator: React.FC<CacheStatusIndicatorProps> = ({
  size = 20,
  show = true,
}) => {
  const theme = useTheme();
  const { status, lastUpdated, isOnline } = useCacheStatus();

  if (!show) {
    return null;
  }

  // Determine icon and color based on status
  const getStatusConfig = () => {
    switch (status) {
      case 'live':
        return {
          icon: <Wifi sx={{ fontSize: size }} />,
          color: theme.palette.success.main,
          tooltip: isOnline
            ? 'Live content - served from network'
            : 'Live content - network available',
          ariaLabel: 'Content is live from network',
        };
      case 'cached':
        return {
          icon: <CloudDone sx={{ fontSize: size }} />,
          color: theme.palette.info.main,
          tooltip: lastUpdated
            ? `Cached content - last updated: ${lastUpdated.toLocaleTimeString()}`
            : 'Cached content - served from local storage',
          ariaLabel: 'Content is cached locally',
        };
      case 'unknown':
      default:
        return {
          icon: <HelpOutline sx={{ fontSize: size }} />,
          color: theme.palette.text.secondary,
          tooltip: 'Cache status unknown',
          ariaLabel: 'Cache status is unknown',
        };
    }
  };

  const { icon, color, tooltip, ariaLabel } = getStatusConfig();

  return (
    <Tooltip title={tooltip} placement="bottom" arrow>
      <span>
        <IconButton
          size="small"
          sx={{
            color,
            padding: 0.5,
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.08)',
            },
          }}
          aria-label={ariaLabel}
          disabled // Make it non-interactive, just an indicator
        >
          {icon}
        </IconButton>
      </span>
    </Tooltip>
  );
};

export default CacheStatusIndicator;
